import { useContext, useState } from 'react';
import { FieldValues } from 'react-hook-form';
import { showModal } from '@/utils/modal.utils';
import { TCustomQueryProps } from '@/type/hooks.types';
import { UseMutationResult } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';
import { ErrorResponse } from '@/utils/axios.http';
import { AllSelectedTableItemsContext } from '@/providers/AllSelectedTableItemsProvider';
import { TFunction } from 'i18next';

interface useDeactivateFieldProps<TSchema extends FieldValues> {
  linkedFieldNames?: string[];
  t?: TFunction<undefined>;
  actionType?: 'deactivate' | 'delete';
  useUpdateData?: (
    objects?: Partial<TCustomQueryProps>
  ) => UseMutationResult<
    AxiosResponse<TSchema[], unknown>,
    ErrorResponse,
    TSchema[],
    unknown
  >;
  useDeleteData?: (
    objects?: Partial<TCustomQueryProps>
  ) => UseMutationResult<
    AxiosResponse<unknown, unknown>,
    ErrorResponse,
    string[],
    unknown
  >;
}

export const useDeactivateField = <TSchema extends FieldValues>({
  t,
  linkedFieldNames,
  actionType = 'deactivate',
  useUpdateData,
  useDeleteData,
}: useDeactivateFieldProps<TSchema>) => {
  const [selectedObjects, setSelectedObjects] = useState<TSchema[]>([]);
  const { setAllTableItemsSelected, setSelectedTableData } = useContext(
    AllSelectedTableItemsContext
  );

  const { mutate: mutateDeactivate, isPending: pendingDeactivate } =
    useUpdateData?.({ t }) || { mutate: () => {}, isPending: false };

  const { mutate: mutateDelete, isPending: pendingDelete } = useDeleteData?.({
    t,
  }) || { mutate: () => {}, isPending: false };

  const handleDeactivate = (objects: TSchema[]) => {
    setSelectedObjects(objects);

    const linkedFieldExists = objects?.some(
      (item) => item?.[linkedFieldNames?.[0] as keyof TSchema]?.length > 0
    );

    if (objects.length > 1) {
      showModal('#deactivation-confirm-modal');
    } else if (objects.length === 1) {
      if (linkedFieldExists) {
        showModal('#information-modal');
      } else {
        showModal('#deactivation-confirm-modal');
      }
    }
  };

  const onConfirmDeactivate = () => {
    if (selectedObjects.length > 0) {
      if (actionType === 'delete') {
        // Delete data - extract IDs from selected objects
        const ids = selectedObjects.map((obj) => obj.id).filter(Boolean);
        mutateDelete(ids);
      } else {
        // Deactivate data (default behavior)
        let deactivatedData = [];

        for (const item of selectedObjects) {
          if (
            !linkedFieldNames ||
            linkedFieldNames?.length === 0 ||
            item[linkedFieldNames?.[0]]?.length === 0
          ) {
            deactivatedData = [
              ...deactivatedData,
              {
                ...item,
                active: false,
              },
            ];
          }
        }

        mutateDeactivate(deactivatedData);
      }

      // Reset selection after action
      setAllTableItemsSelected(false);
      setSelectedTableData([]);
      setSelectedObjects([]);
    }
  };

  return {
    numberOfItemsToDeactivate: selectedObjects.length,
    pendingDeactivate:
      actionType === 'delete' ? pendingDelete : pendingDeactivate,
    handleDeactivate,
    onConfirmDeactivate,
  };
};
